/**
 * InboxBulkActionBar
 *
 * Subtle inline toolbar that appears above the message list when items are selected.
 * Shows select-all checkbox, selected count, and context-aware action buttons.
 */

import {
  CheckIcon,
  ArchiveBoxIcon,
  TrashIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline';
import { Spinner } from '../../../components/ui/Spinner';
import type { UnifiedInboxView } from '../hooks/useUnifiedInbox';
import type { BulkActionType } from '../../../types/inbox.types';

interface InboxBulkActionBarProps {
  selectedCount: number;
  isAllSelected: boolean;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onBulkAction: (action: BulkActionType) => void;
  view: UnifiedInboxView;
  isLoading: boolean;
  loadingAction?: BulkActionType | null;
}

export function InboxBulkActionBar({
  selectedCount,
  isAllSelected,
  onSelectAll,
  onClearSelection,
  onBulkAction,
  view,
  isLoading,
  loadingAction,
}: InboxBulkActionBarProps) {
  const handleSelectAllToggle = () => {
    if (isAllSelected) {
      onClearSelection();
    } else {
      onSelectAll();
    }
  };

  return (
    <div
      className="flex items-center gap-3 px-4 py-2.5 border-b"
      style={{ backgroundColor: 'var(--color-bg-secondary, #f8fafc)', borderColor: 'var(--color-border)' }}
    >
      {/* Select All Checkbox */}
      <input
        type="checkbox"
        checked={isAllSelected}
        onChange={handleSelectAllToggle}
        className="h-4 w-4 rounded text-primary-600 focus:ring-primary-500 cursor-pointer"
        style={{ borderColor: 'var(--color-border)' }}
      />

      {/* Selected Count */}
      <span className="text-sm font-semibold text-primary-600">
        {selectedCount} selected
      </span>

      {/* Action Buttons */}
      <div className="flex items-center gap-1 ml-2">
        {/* Inbox view actions */}
        {view === 'inbox' && (
          <>
            <ActionButton
              icon={CheckIcon}
              label="Mark as Read"
              onClick={() => onBulkAction('read')}
              isLoading={isLoading && loadingAction === 'read'}
              disabled={isLoading}
            />
            <ActionButton
              icon={ArchiveBoxIcon}
              label="Archive"
              onClick={() => onBulkAction('archive')}
              isLoading={isLoading && loadingAction === 'archive'}
              disabled={isLoading}
            />
            <ActionButton
              icon={TrashIcon}
              label="Delete"
              onClick={() => onBulkAction('delete')}
              isLoading={isLoading && loadingAction === 'delete'}
              disabled={isLoading}
              variant="danger"
            />
          </>
        )}

        {/* Archived view actions */}
        {view === 'archived' && (
          <>
            <ActionButton
              icon={ArrowUturnLeftIcon}
              label="Move to Inbox"
              onClick={() => onBulkAction('unarchive')}
              isLoading={isLoading && loadingAction === 'unarchive'}
              disabled={isLoading}
            />
            <ActionButton
              icon={TrashIcon}
              label="Delete"
              onClick={() => onBulkAction('delete')}
              isLoading={isLoading && loadingAction === 'delete'}
              disabled={isLoading}
              variant="danger"
            />
          </>
        )}

        {/* Deleted view actions */}
        {view === 'deleted' && (
          <ActionButton
            icon={ArrowUturnLeftIcon}
            label="Restore"
            onClick={() => onBulkAction('restore')}
            isLoading={isLoading && loadingAction === 'restore'}
            disabled={isLoading}
          />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// ACTION BUTTON
// ============================================================================

interface ActionButtonProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  variant?: 'default' | 'danger';
}

function ActionButton({
  icon: Icon,
  label,
  onClick,
  isLoading,
  disabled,
  variant = 'default',
}: ActionButtonProps) {
  const colorClasses =
    variant === 'danger'
      ? 'text-red-500 border-red-200 hover:bg-red-50'
      : 'text-gray-600 border-gray-200 hover:bg-gray-100';

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded border transition-colors disabled:opacity-50 ${colorClasses}`}
      title={label}
    >
      {isLoading ? (
        <Spinner size="sm" />
      ) : (
        <Icon className="h-3.5 w-3.5" />
      )}
      {label}
    </button>
  );
}
